import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, Megaphone, Package } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Loader from "../../components/Loader/Loader";
import Notification from "../../components/Notification/Notification";

/* ===================== SERVICES / UTILS ===================== */
import { apiGet } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";
import { getRole, isLoggedIn } from "../../utils/auth";
import { formatDate } from "../Admin/FeatureUpdates/featureUtils";

/* ===================== STYLES ===================== */
import styles from "./FeatureUpdateView.module.scss";

/* Client-facing page for a single published feature update. Users land here
   from the "View Update" button in the notification sidebar (pageUrl). */
const FeatureUpdateView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  /* "Client" org users (anyone not Internal) — same convention as the rest
     of the app, which only ever branches on userType === "Internal". */
  const isClient = isLoggedIn() && getRole() !== "Internal";

  const [featureUpdate, setFeatureUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState("");

  /* -------- client-only guard -------- */
  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/login", { replace: true });
    } else if (!isClient) {
      // Internal (super-admin) users manage updates from the admin section.
      navigate("/admin/product-access/features", { replace: true });
    }
  }, [isClient, navigate]);

  const fetchUpdate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", `/feature-updates/${id}`);
      const fu = res?.data?.featureUpdate;
      if (res?.status === "success" && fu) {
        setFeatureUpdate(fu);
      } else {
        setNotification(res?.message || "Feature update not found");
      }
    } catch (error) {
      setNotification(getErrorMessage(error, "Feature update not found"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isClient) fetchUpdate();
  }, [isClient, fetchUpdate]);

  if (!isClient) return null;

  const products = featureUpdate?.products || [];
  const media = featureUpdate?.media || [];

  return (
    <div className={styles["fuv-page"]}>
      {notification && !loading && !featureUpdate && (
        <div className={styles["fuv-toast"]}>
          <Notification message={notification} type="error" />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["fuv-header"]}>
        <button
          type="button"
          className={styles["fuv-back"]}
          onClick={() => navigate(-1)}
          title="Go back"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <div className={styles["fuv-icon-wrapper"]}>
          <Megaphone size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["fuv-title-group"]}>
          <h2 className={styles["fuv-title"]}>Feature Update</h2>
          <p className={styles["fuv-subtitle"]}>
            See what&apos;s new in your products.
          </p>
        </div>
      </div>

      {loading ? (
        <div className={styles["fuv-card"]}>
          <Loader />
        </div>
      ) : !featureUpdate ? (
        <div className={styles["fuv-card"]}>
          <div className={styles["fuv-empty"]}>
            This update is not available. It may have been removed.
          </div>
        </div>
      ) : (
        <>
          {/* SUMMARY */}
          <div className={styles["fuv-card"]}>
            <div className={styles["fuv-card-header"]}>
              <div className={styles["fuv-card-icon"]}>
                <Megaphone size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["fuv-card-title"]}>What&apos;s New</h3>
              {featureUpdate.publishedAt && (
                <span className={styles["fuv-date"]}>
                  <CalendarClock size={14} strokeWidth={2.5} />
                  {formatDate(featureUpdate.publishedAt)}
                </span>
              )}
            </div>

            <h4 className={styles["fuv-subject"]}>{featureUpdate.title}</h4>
            <p className={styles["fuv-description"]}>
              {featureUpdate.description}
            </p>
          </div>

          {/* PRODUCTS */}
          {products.length > 0 && (
            <div className={styles["fuv-card"]}>
              <div className={styles["fuv-card-header"]}>
                <div className={styles["fuv-card-icon"]}>
                  <Package size={18} strokeWidth={2.5} />
                </div>
                <h3 className={styles["fuv-card-title"]}>Applies To</h3>
              </div>
              <div className={styles["fuv-pills"]}>
                {products.map((p, i) => (
                  <span key={p.id ?? i} className={styles["fuv-pill"]}>
                    {p.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* MEDIA */}
          {media.length > 0 && (
            <div className={styles["fuv-card"]}>
              <div className={styles["fuv-media-grid"]}>
                {media.map((m) =>
                  m.type === "video" ? (
                    <video
                      key={m.id}
                      src={m.url}
                      controls
                      className={styles["fuv-media"]}
                    />
                  ) : (
                    <a
                      key={m.id}
                      href={m.url}
                      target="_blank"
                      rel="noreferrer"
                      className={styles["fuv-media-link"]}
                    >
                      <img src={m.url} alt="" className={styles["fuv-media"]} />
                    </a>
                  ),
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FeatureUpdateView;
